import React, { useEffect, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";
import { DEFAULT_CENTER } from "../../lib/constants";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const TRAFFIC_COLORS = { NORMAL: "#16A34A", SLOW: "#F59E0B", TRAFFIC_JAM: "#DC2626" };
const ROUTE_COLOR = "#1D4ED8";

// Compass bearing (deg, clockwise from north) from point a to b.
const bearing = (a, b) => {
  if (!a || !b) return 0;
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const dLon = toRad(b.lng - a.lng);
  const y = Math.sin(dLon) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// SVG teardrop pin as a data-URI icon for google.maps.Marker.
const pinIcon = (color) => ({
  url:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg viewBox="0 0 28 36" width="28" height="36" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.2 0 13.9 0 24.3 14 36 14 36s14-11.7 14-22.1C28 6.2 21.7 0 14 0z" fill="${color}"/><circle cx="14" cy="14" r="5.5" fill="#FFFFFF"/></svg>`,
    ),
  anchorX: 14,
  anchorY: 34,
  w: 28,
  h: 36,
});

// Idle drivers: labelled circle. Active (moving) driver: brand circle with a white
// navigation arrow rotated to the travel heading.
const vehicleIcon = (type, active = false, heading = 0, eta = null, highlight = false) => {
  if (active) {
    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><circle cx="17" cy="17" r="15" fill="#1D4ED8" stroke="#FFFFFF" stroke-width="2"/><g transform="rotate(${Math.round(heading)},17,17)"><path d="M17 6 L24 25 L17 20 L10 25 Z" fill="#FFFFFF"/></g></svg>`,
        ),
      anchorX: 17,
      anchorY: 17,
      w: 34,
      h: 34,
    };
  }
  const label = type === "car" ? "CAR" : type === "auto" ? "AUT" : type === "jumbo" ? "JMB" : "SC";
  const etaTxt = (eta != null && Number.isFinite(eta)) ? `~${Math.max(1, Math.round(eta))}m` : null;
  if (highlight) {
    // Nearest driver: larger brand-blue dot with a soft pulse ring and a prominent ETA pill.
    const w = Math.max(50, 16 + (etaTxt ? etaTxt.length : 0) * 7);
    const h = etaTxt ? 58 : 42;
    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><circle cx="${w / 2}" cy="20" r="18" fill="#1D4ED8" opacity="0.16"/><circle cx="${w / 2}" cy="20" r="12.5" fill="#1D4ED8" stroke="#FFFFFF" stroke-width="2.5"/><text x="${w / 2}" y="23.5" font-family="system-ui,Arial" font-size="8.5" font-weight="800" fill="#FFFFFF" text-anchor="middle">${label}</text>${etaTxt ? `<rect x="${w / 2 - (etaTxt.length * 3.6 + 7)}" y="42" rx="7" ry="7" width="${etaTxt.length * 7.2 + 14}" height="15" fill="#1D4ED8"/><text x="${w / 2}" y="52.5" font-family="system-ui,Arial" font-size="9" font-weight="800" fill="#FFFFFF" text-anchor="middle">${etaTxt}</text>` : ""}</svg>`,
        ),
      anchorX: w / 2,
      anchorY: 20,
      w,
      h,
    };
  }
  if (etaTxt) {
    const w = Math.max(34, 10 + etaTxt.length * 7);
    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="44" viewBox="0 0 ${w} 44"><circle cx="${w / 2}" cy="15" r="13" fill="#111827" stroke="#FFFFFF" stroke-width="2"/><text x="${w / 2}" y="18" font-family="system-ui,Arial" font-size="9" font-weight="700" fill="#FFFFFF" text-anchor="middle">${label}</text><rect x="${w / 2 - (etaTxt.length * 3.4 + 5)}" y="30" rx="6" ry="6" width="${etaTxt.length * 6.8 + 10}" height="13" fill="#1D4ED8"/><text x="${w / 2}" y="39.5" font-family="system-ui,Arial" font-size="8.5" font-weight="700" fill="#FFFFFF" text-anchor="middle">${etaTxt}</text></svg>`,
        ),
      anchorX: w / 2,
      anchorY: 15,
      w,
      h: 44,
    };
  }
  return {
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30"><circle cx="15" cy="15" r="13" fill="#111827" stroke="#FFFFFF" stroke-width="2"/><text x="15" y="18" font-family="system-ui,Arial" font-size="9" font-weight="700" fill="#FFFFFF" text-anchor="middle">${label}</text></svg>`,
      ),
    anchorX: 15,
    anchorY: 15,
    w: 30,
    h: 30,
  };
};

function toGIcon(icon) {
  const g = window.google.maps;
  return {
    url: icon.url,
    scaledSize: new g.Size(icon.w, icon.h),
    anchor: new g.Point(icon.anchorX, icon.anchorY),
  };
}

function Layers({ pickup, drop, drivers, activeDriver, rider, route, traffic, follow }) {
  const map = useMap();
  const objsRef = useRef([]);

  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    objsRef.current.forEach((o) => o.setMap(null));
    objsRef.current = [];
    const add = (o) => {
      objsRef.current.push(o);
      return o;
    };
    const bounds = new g.LatLngBounds();
    const extend = (p) => p && Number.isFinite(p.lat) && bounds.extend({ lat: p.lat, lng: p.lng });

    if (pickup) {
      add(new g.Marker({ position: pickup, icon: toGIcon(pinIcon("#111827")), map, title: "Pickup" }));
      extend(pickup);
    }
    if (drop) {
      add(new g.Marker({ position: drop, icon: toGIcon(pinIcon(ROUTE_COLOR)), map, title: "Drop" }));
      extend(drop);
    }
    if (pickup && drop) {
      add(
        new g.Polyline({
          path: [pickup, drop],
          map,
          strokeColor: "#111827",
          strokeOpacity: 0,
          icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 0.35, scale: 3 }, offset: "0", repeat: "14px" }],
        }),
      );
    }
    const roadPts = route && route.length >= 2 && route.every((p) => p && Number.isFinite(p.lat)) ? route : null;
    if (roadPts) {
      if (traffic && traffic.length) {
        // Colour each live-traffic interval; draw a faint base line underneath for gaps.
        add(new g.Polyline({ path: roadPts, map, strokeColor: ROUTE_COLOR, strokeWeight: 4, strokeOpacity: 0.35 }));
        traffic.forEach((seg) => {
          const slice = roadPts.slice(Math.max(0, seg.start), Math.min(roadPts.length, seg.end + 1));
          if (slice.length >= 2) {
            add(new g.Polyline({ path: slice, map, strokeColor: TRAFFIC_COLORS[seg.speed] || ROUTE_COLOR, strokeWeight: 5, strokeOpacity: 0.95, zIndex: 2 }));
          }
        });
      } else {
        add(new g.Polyline({ path: roadPts, map, strokeColor: ROUTE_COLOR, strokeWeight: 4, strokeOpacity: 0.9 }));
      }
      roadPts.forEach(extend);
    }
    if (!activeDriver) {
      const list = drivers || [];
      let closestIdx = -1, best = Infinity;
      list.forEach((d, i) => {
        if (!Number.isFinite(d.lat)) return;
        const m = Number.isFinite(d.eta_min) ? d.eta_min : (Number.isFinite(d.distance_km) ? d.distance_km : Infinity);
        if (m < best) { best = m; closestIdx = i; }
      });
      list.forEach((d, i) => {
        if (!Number.isFinite(d.lat)) return;
        const near = i === closestIdx;
        add(new g.Marker({
          position: d,
          icon: toGIcon(vehicleIcon(d.vehicle_type, false, 0, d.eta_min, near)),
          map,
          title: near ? `Nearest driver${d.eta_min ? ` · ~${Math.round(d.eta_min)} min away` : ""}` : (d.eta_min ? `~${Math.round(d.eta_min)} min away` : undefined),
          zIndex: near ? 4 : 1,
        }));
      });
    }
    if (activeDriver && Number.isFinite(activeDriver.lat)) {
      // Heading: prefer the next road point ahead, else aim at drop/pickup.
      const ahead = roadPts && roadPts.length > 1 ? roadPts[1] : drop || pickup;
      const hd = Number.isFinite(activeDriver.heading) ? activeDriver.heading : bearing(activeDriver, ahead);
      add(
        new g.Marker({
          position: activeDriver,
          icon: toGIcon(vehicleIcon(activeDriver.vehicle_type, true, hd)),
          map,
          title: activeDriver.name,
          zIndex: 3,
        }),
      );
      if (!follow) extend(activeDriver);
    }
    if (rider && Number.isFinite(rider.lat)) {
      add(new g.Marker({ position: rider, icon: toGIcon(pinIcon("#2563EB")), map, title: rider.name || "Rider", zIndex: 2 }));
      extend(rider);
    }

    if (!bounds.isEmpty()) {
      const span = bounds.toSpan();
      if (span.lat() < 0.002 && span.lng() < 0.002) map.setCenter(bounds.getCenter()), map.setZoom(15);
      else map.fitBounds(bounds, 50);
    }
    // Some containers (e.g. the public share page) get their final height from CSS just
    // after the map initializes, leaving Google showing blank tiles. Nudge it to resize and
    // re-fit once layout has settled.
    const rt = setTimeout(() => {
      if (!map || !window.google) return;
      window.google.maps.event.trigger(map, "resize");
      if (!bounds.isEmpty()) {
        const sp = bounds.toSpan();
        if (sp.lat() < 0.002 && sp.lng() < 0.002) { map.setCenter(bounds.getCenter()); map.setZoom(15); }
        else map.fitBounds(bounds, 50);
      }
    }, 400);
    return () => clearTimeout(rt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, JSON.stringify({ pickup, drop, drivers, activeDriver, rider, route, traffic })]);

  // Keep a moving driver inside the viewport when following.
  useEffect(() => {
    if (!map || !follow || !activeDriver || !Number.isFinite(activeDriver.lat)) return;
    const b = map.getBounds();
    if (b && !b.contains({ lat: activeDriver.lat, lng: activeDriver.lng })) map.panTo({ lat: activeDriver.lat, lng: activeDriver.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, follow, activeDriver?.lat, activeDriver?.lng]);

  return null;
}

function HeatLayer({ points, opacity = 0.6 }) {
  const map = useMap();
  const circlesRef = useRef([]);
  useEffect(() => {
    if (!map || !window.google) return undefined;
    const g = window.google.maps;
    const pts = (points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    const maxW = pts.reduce((m, p) => Math.max(m, p.weight || 1), 1);
    circlesRef.current.forEach((c) => c.setMap(null));
    // Soft weighted circles approximate a heatmap using only the core Maps API
    // (google.maps.visualization.HeatmapLayer was removed in Maps JS v3.65).
    circlesRef.current = pts.map((p) => {
      const norm = Math.min(1, (p.weight || 1) / maxW);
      const hue = Math.round(45 - 45 * norm); // amber (low) -> red (high)
      return new g.Circle({
        map,
        center: { lat: p.lat, lng: p.lng },
        radius: 320 + 260 * norm,
        strokeOpacity: 0,
        fillColor: `hsl(${hue}, 92%, 50%)`,
        fillOpacity: Math.max(0.08, Math.min(0.72, opacity * (0.45 + 0.55 * norm))),
        clickable: false,
        zIndex: 1,
      });
    });
    return () => { circlesRef.current.forEach((c) => c.setMap(null)); circlesRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, opacity, JSON.stringify(points)]);
  return null;
}

class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* keep the dashboard alive even if the map fails */ }
  render() {
    if (this.state.failed) {
      return (
        <div className={`flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-2xl border border-surface-300 bg-surface-100 ${this.props.className || ""}`} data-testid="ride-map">
          <MapPin className="h-6 w-6 text-brand" />
          <p className="text-sm text-ink-muted">Map unavailable</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function RideMap({ pickup, drop, drivers = [], activeDriver, rider = null, route = null, traffic = [], follow = false, heatmapPoints = null, heatOpacity = 0.6, className = "" }) {
  const center = pickup || drop || DEFAULT_CENTER;
  if (!KEY) {
    return (
      <div className={`flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-2xl border border-surface-300 bg-surface-100 ${className}`} data-testid="ride-map">
        <MapPin className="h-6 w-6 text-brand" />
        <p className="text-sm text-ink-muted">Map unavailable</p>
      </div>
    );
  }
  return (
    <MapErrorBoundary className={className}>
      <div className={`overflow-hidden rounded-2xl border border-surface-300 ${className}`} data-testid="ride-map">
        <APIProvider apiKey={KEY}>
          <Map
            defaultCenter={{ lat: center.lat, lng: center.lng }}
            defaultZoom={13}
            style={{ width: "100%", height: "100%", minHeight: 320 }}
            gestureHandling="greedy"
            disableDefaultUI
            zoomControl
            clickableIcons={false}
          >
            <Layers pickup={pickup} drop={drop} drivers={drivers} activeDriver={activeDriver} rider={rider} route={route} traffic={traffic} follow={follow} />
            {heatmapPoints ? <HeatLayer points={heatmapPoints} opacity={heatOpacity} /> : null}
          </Map>
        </APIProvider>
      </div>
    </MapErrorBoundary>
  );
}
